// External Libraries
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

// Styles
import '../styles/Profile.css';

// Assets
import LeftArrow from '../assets/left-arrow.png';
import Edit from '../assets/edit.png';
import GoogleScholar from '../assets/google-scholar.png';
import LinkedIn from '../assets/linkedin-input.png';
import LinkIcon from '../assets/link-icon.png';
import NoPhoto from '../assets/no-photo.png';
import Location from '../assets/location.png';

// Constants
import { APPROVED, FACULTY } from '../constants/Constants';

// Redux/State Management
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';

// API Services
import { getUserDetails } from '../api/UserService';

// Types
import {
  EmailThreadProps,
  UserExperience,
  UserDetailsRes,
  UserGrant,
  UserPatent,
  UserPublication,
  UserIdea,
  UserProject,
} from '../types/types';

// Utilities
import {
  formatDate,
  getUserTypeLogo,
  replaceNewlinesWithBr,
  timeAgo,
} from '../utils/CommonUtils';

// Components
import Loading from './common/Loading';
import { RedirectLink } from './common/RedirectLink';
import Button from './common/Button';
import Conversation from './Conversation';
import Text from './common/Text';
import useScreenSize from '../hooks/useScreenSize';

const Profile: React.FC = () => {
  const user = useSelector((state: RootState) => state.user);
  const schools = useSelector((state: RootState) => state.schools);
  const departments = useSelector((state: RootState) => state.departments);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const numericId = id ? parseInt(id, 10) : undefined;
  const [userInfo, setUserInfo] = useState<UserDetailsRes | null>();
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState<boolean | null>(null);
  const [userTypeId, setUserTypeId] = useState<number | null>(null);
  const [isPopupVisible, setPopupVisible] = useState<boolean>(false);
  const [conversationDetails, setConversationDetails] =
    useState<EmailThreadProps | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isBioExpanded, setIsBioExpanded] = useState<boolean>(false);
  const [isBioTruncated, setIsBioTruncated] = useState<boolean>(false);
  const isMobile = useScreenSize('(max-width:576px)');
  const profileBioRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const verifyBioTruncation = () => {
      if (profileBioRef.current) {
        setIsBioTruncated(
          profileBioRef.current.scrollHeight >
            profileBioRef.current.clientHeight,
        );
      }
    };
    verifyBioTruncation();
  }, [userInfo?.bio]);

  const handleToggleBio = () => {
    setIsBioExpanded((prev) => !prev);
  };

  const handleContactClick = (email: string, recipientName: string) => {
    setConversationDetails({
      emailAddress: email,
      recipientName: recipientName,
    });
    setPopupVisible(true);
  };

  const handleBlurClick = () => {
    setPopupVisible(false);
    setConversationDetails(null);
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (numericId !== undefined) {
        const result = await getUserDetails(numericId);
        if ('code' in result) {
          // Handle the InternalServerError
          console.error('Error fetching UserDetails:', result);
          if (result.code === 'NOT_FOUND') {
            setMessage('The user might have been deleted or never created.');
          } else if (result.code === 'FORBIDDEN') {
            setMessage(result.message);
          } else {
            setMessage(result.message);
          }
          setLoading(false);
        } else {
          // Handle the successful response
          const resUserInfo = result as UserDetailsRes;
          setUserInfo(resUserInfo);
          setLoading(false);
          setMessage(null);
          setUserTypeId(resUserInfo.user_type_id);
          if (id && user.userDetails?.userId === parseInt(id)) {
            setCanEdit(true);
          } else {
            setCanEdit(false);
          }
          resUserInfo.user_type_id;
        }
      }
    };
    fetchUserDetails();
  }, [id, numericId, user.userDetails?.userId]);

  const citations = () => {
    return (
      <>
        <div className="section-break" />
        <div className="profile-sub-container grid gap-14">
          <div className="profile-sub-heading-title">
            Publications
            {userInfo?.publications &&
              userInfo?.publications.length !== 0 &&
              userInfo?.total_citations !== 0 && (
                <div className="my-profile-citations-count-date">
                  <Text className="my-profile-citations-count">
                    {userInfo?.total_citations} citations
                  </Text>
                  <Text className="my-profile-citations-date">
                    Since {userInfo?.oldest_publication_year}
                  </Text>
                </div>
              )}
          </div>

          <div className="line" />
          {userInfo?.publications?.map((pub: UserPublication, index) => (
            <div key={index} className="grid padding-x-20">
              <div className="grid">
                <Text className="mp-citation-title">{pub.title}</Text>
                <Text className="mp-citation-authors">{pub.authors}</Text>
              </div>
              {userInfo?.publications &&
                index < userInfo?.publications.length - 1 && (
                  <div className="line light-line mp-hr" />
                )}
            </div>
          ))}
          {userInfo?.google_scholar_url &&
            userInfo?.publications &&
            userInfo?.publications.length !== 0 && (
              <div className="padding-x-20">
                <a
                  href={userInfo?.google_scholar_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={userInfo.google_scholar_url}
                >
                  <RedirectLink
                    text="See more on Google Scholar"
                    icon={LinkIcon}
                  />
                </a>
              </div>
            )}
          {userInfo?.linkedin_url &&
            userInfo?.publications &&
            userInfo?.publications.length !== 0 && (
              <div className="padding-x-20">
                <a
                  href={userInfo?.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={userInfo.linkedin_url}
                >
                  <RedirectLink text="See more on LinkedIn" icon={LinkIcon} />
                </a>
              </div>
            )}
        </div>
      </>
    );
  };

  const grants = () => {
    return (
      <>
        <div className="section-break" />
        <div className="profile-sub-container grid gap-14">
          <div className="profile-sub-heading-title">Grants</div>
          <div className="line" />
          {userInfo?.grants?.map((grant: UserGrant, index) => (
            <div key={index} className="padding-x-20">
              <div>
                <div className="grid">
                  <Text className="medium-bold">{grant.organization}</Text>
                  {grant.grant_number && (
                    <Text className="mp-grant-number">
                      {grant.grant_number}
                    </Text>
                  )}
                  <Text className="mp-grant-number">{grant.title}</Text>
                </div>
                <Text className="mp-grant-description">
                  {grant.description}
                </Text>
              </div>
              {userInfo?.grants && index < userInfo?.grants.length - 1 && (
                <div className="line light-line mp-hr" />
              )}
            </div>
          ))}
          {userInfo?.grants &&
            userInfo?.grants.length !== 0 &&
            userInfo.faculty_page_url && (
              <div className="padding-x-20">
                <a
                  href={userInfo.faculty_page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={userInfo.faculty_page_url}
                >
                  <RedirectLink text="See more on Faculty" icon={LinkIcon} />
                </a>
              </div>
            )}
        </div>
      </>
    );
  };

  const patents = () => {
    return (
      <>
        <div className="section-break" />
        <div className="profile-sub-container grid gap-14">
          <div className="profile-sub-heading-title">Patents</div>
          <div className="line" />
          {userInfo?.patents?.map((patent: UserPatent, index) => (
            <div key={index}>
              <div className="grid gap-15 padding-x-20">
                <Text className="medium-bold">{patent.title}</Text>
                <Text className="mp-patent-number-date">
                  {patent.patent_number +
                    ' . Issued ' +
                    formatDate(patent.issue_date)}
                </Text>
              </div>
              {userInfo?.patents && index < userInfo?.patents.length - 1 && (
                <div className="line light-line mp-hr" />
              )}
            </div>
          ))}
          {userInfo?.patents &&
            userInfo?.patents.length !== 0 &&
            userInfo.linkedin_url && (
              <div className="padding-x-20">
                <a
                  href={userInfo.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={userInfo.linkedin_url}
                >
                  <RedirectLink text="See more on LinkedIn" icon={LinkIcon} />
                </a>
              </div>
            )}
        </div>
      </>
    );
  };

  const formatDateRanges = (
    startDate: string | null | undefined,
    endDate: string | null | undefined,
  ) => {
    const start = new Date(startDate || '');
    if (isNaN(start.getTime())) return '';

    const options: Intl.DateTimeFormatOptions = {
      month: 'long',
      year: 'numeric',
    };
    const formattedStart = start.toLocaleDateString(undefined, options);

    let formattedEnd = '';
    if (endDate) {
      formattedEnd =
        endDate === 'Present'
          ? 'Present'
          : new Date(endDate || '').toLocaleDateString(undefined, options);
    }
    return formattedEnd
      ? `${formattedStart} - ${formattedEnd}`
      : formattedStart;
  };

  const projects = () => {
    return (
      <>
        <div className="section-break" />
        <div className="profile-sub-container grid gap-14">
          <div className=" profile-sub-heading-title">Projects</div>
          <div className="line" />
          {userInfo?.projects?.map((project: UserProject, index) => (
            <div key={index}>
              <div className="grid gap-15 padding-x-20">
                <div className="grid">
                  <Text className="medium-bold">{project?.title}</Text>
                  {(project.start_date !== null ||
                    project.end_date !== null) && (
                    <Text className="my-profile-education-deg">
                      {formatDateRanges(
                        project.start_date,
                        project.end_date === null
                          ? 'Present'
                          : project.end_date,
                      )}
                    </Text>
                  )}
                </div>
                {project?.description && <Text>{project?.description}</Text>}
              </div>
              {userInfo?.projects && index < userInfo?.projects.length - 1 && (
                <div className="line light-line mp-hr" />
              )}
            </div>
          ))}

          {userInfo?.linkedin_url && (
            <div className="padding-x-20">
              <a
                href={userInfo?.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                key={userInfo?.linkedin_url}
              >
                <RedirectLink text="See more on LinkedIn" icon={LinkIcon} />
              </a>
            </div>
          )}
        </div>
      </>
    );
  };

  const getYearFromDate = (dateString: string): number => {
    const date = new Date(dateString);
    return date.getFullYear();
  };

  const education = () => {
    return (
      <>
        <div className="section-break" />
        <div className="profile-sub-container grid gap-14">
          <div className="profile-sub-heading-title">Education</div>
          <div className="line" />
          {userInfo?.education !== undefined &&
            userInfo?.education !== null && (
              <>
                <div className="grid custom-gap-5 padding-x-20">
                  <Text className="medium-bold">
                    {userInfo?.education?.institution}
                  </Text>
                  <Text className="my-profile-education-deg">
                    {userInfo?.education?.degree}
                  </Text>
                  {userInfo?.education?.start_year &&
                    (userInfo?.education?.start_year ||
                      userInfo?.education?.end_year) && (
                      <Text className="my-profile-education-deg">
                        {((userInfo?.education?.start_year &&
                          getYearFromDate(userInfo?.education?.start_year)) ??
                          '') +
                          (userInfo?.education?.end_year ? ' - ' : '') +
                          ((userInfo?.education?.end_year &&
                            getYearFromDate(userInfo?.education?.end_year)) ??
                            '')}
                      </Text>
                    )}
                  {userInfo?.education?.activities && (
                    <Text className="my-profile-education-deg">
                      Activities and societies:{' '}
                      {userInfo?.education?.activities}
                    </Text>
                  )}
                </div>
                {userInfo?.linkedin_url && (
                  <div className="padding-x-20">
                    <a
                      href={userInfo?.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      key={userInfo?.linkedin_url}
                    >
                      <RedirectLink
                        text="See more on LinkedIn"
                        icon={LinkIcon}
                      />
                    </a>
                  </div>
                )}
                {userInfo?.linkedin_url === null &&
                  userInfo?.faculty_page_url &&
                  userInfo.user_type_id === FACULTY && (
                    <div className="padding-x-20">
                      <a
                        href={userInfo?.faculty_page_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={userInfo?.faculty_page_url}
                      >
                        <RedirectLink
                          text="See more on Faculty Page"
                          icon={LinkIcon}
                        />
                      </a>
                    </div>
                  )}
              </>
            )}
        </div>
      </>
    );
  };

  const formatDateRange = (
    startDate: string,
    endDate?: string | null,
  ): string => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date(); // Use current date if endDate is null

    // Format start date
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
    };
    const formattedStart = start.toLocaleDateString('en-US', options);
    const formattedEnd = endDate
      ? end.toLocaleDateString('en-US', options)
      : 'Present';

    // Calculate duration
    const yearsDifference = end.getFullYear() - start.getFullYear();
    const monthsDifference = end.getMonth() - start.getMonth();
    const totalMonths = yearsDifference * 12 + monthsDifference; // Total months calculation

    // Calculate years and remaining months
    const totalYears = Math.floor(totalMonths / 12);
    const remainingMonths = totalMonths % 12;

    // Prepare the duration string
    let durationString = `${totalYears} yr${totalYears !== 1 ? 's' : ''}`;
    if (remainingMonths > 0) {
      durationString += ` ${remainingMonths} mo${remainingMonths !== 1 ? 's' : ''}`;
    }

    // Return formatted string
    return `${formattedStart} - ${formattedEnd} · ${durationString}`;
  };

  const mostRecentExperiences = () => {
    return (
      <>
        <div className="section-break" />
        <div className="profile-sub-container grid gap-14">
          <div className="profile-sub-heading-title">Experience</div>
          <div className="line" />
          {userInfo?.most_recent_experience?.map(
            (exp: UserExperience, index) => (
              <div key={index} className="grid custom-gap-5 padding-x-20">
                <Text className="medium-bold">{exp?.title}</Text>
                <Text className="my-profile-education-deg">
                  {exp?.organization +
                    (exp?.employment_type ? ' - ' + exp.employment_type : '')}
                </Text>
                {exp?.start_date ||
                  (exp?.end_date && (
                    <Text className="my-profile-education-deg">
                      {exp &&
                        exp?.start_date !== null &&
                        formatDateRange(exp?.start_date, exp?.end_date)}
                    </Text>
                  ))}

                {exp?.description && (
                  <Text className="my-profile-education-deg">
                    {exp?.description}
                  </Text>
                )}

                {userInfo?.most_recent_experience &&
                  index < userInfo?.most_recent_experience.length - 1 && (
                    <div className="line light-line mp-hr" />
                  )}
              </div>
            ),
          )}
          {userInfo?.linkedin_url && (
            <div className="padding-x-20">
              <a
                href={userInfo?.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                key={userInfo?.linkedin_url}
              >
                <RedirectLink text="See more on LinkedIn" icon={LinkIcon} />
              </a>
            </div>
          )}
          {userInfo?.linkedin_url === null &&
            userInfo?.faculty_page_url &&
            userInfo.user_type_id === FACULTY && (
              <div className="padding-x-20">
                <a
                  href={userInfo?.faculty_page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={userInfo?.faculty_page_url}
                >
                  <RedirectLink
                    text="See more on Faculty Page"
                    icon={LinkIcon}
                  />
                </a>
              </div>
            )}
        </div>
      </>
    );
  };

  const ideas = () => {
    return (
      <>
        <div className="section-break" />
        <div className="profile-sub-container grid gap-14">
          <Text className="profile-sub-heading-title">Ideas</Text>
          <div className="line" />
          {userInfo?.ideas?.map((idea: UserIdea, index) => (
            <div key={index} className="grid padding-x-20">
              <Link to={`/ideas/${idea.id}`} className="redirect-link">
                <Text className="medium-bold">{idea.title}</Text>
              </Link>
              <Text className="mp-grant-number">
                {timeAgo(idea.created_at) === 'Invalid date'
                  ? ''
                  : timeAgo(idea.created_at)}
              </Text>
              {userInfo?.ideas && index < userInfo?.ideas.length - 1 && (
                <div className="line mp-hr" />
              )}
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderConversationButton = (className?: string) => {
    if (canEdit) {
      return (
        <div className="conversation-button-container">
          <Button
            className={`${className} disabled-button`}
            disabled={true}
            type="button"
          >
            Start a Conversation
          </Button>
        </div>
      );
    }

    if (
      userInfo &&
      userInfo.user_status_id === APPROVED &&
      userInfo.admin_status_id === APPROVED
    ) {
      return (
        <div className="conversation-button-container">
          <Button
            onClick={() => {
              if (userInfo!.email && userInfo!.first_name) {
                handleContactClick(userInfo!.email, userInfo!.first_name);
              }
            }}
            className={`${className} tertiary-button`}
            disabled={false}
            type="button"
          >
            Start a Conversation
          </Button>
        </div>
      );
    }

    return null;
  };

  const renderEditButton = () => {
    if (canEdit) {
      return (
        <Button
          type="button"
          className="edit"
          onClick={() => navigate('/update-profile')}
        >
          <img src={Edit} alt="edit" className="edit-icon" />
          <div className="input-label">Edit</div>
        </Button>
      );
    } else {
      return (
        <div className="not-edit d-sm-block">
          {userTypeId && getUserTypeLogo(userTypeId)}
        </div>
      );
    }
  };

  return (
    <>
      {loading ? (
        <div className="loading-class">
          <Loading color="var(--black)" />
        </div>
      ) : userInfo && message === null ? (
        <div className="custom-background">
          <div className="my-profile-image-container">
            <div className="my-user-info-container">
              <img
                src={
                  userInfo.profile_image_url &&
                  userInfo.profile_image_url.trim() !== ''
                    ? userInfo.profile_image_url
                    : NoPhoto
                }
                alt={userInfo.first_name}
                className="my-profile-photo"
              />
              {!isMobile && renderConversationButton()}
            </div>
            <div className="my-profile-nav-bar">
              <Button
                type="button"
                className="my-profile-nav-button"
                onClick={() => {
                  navigate(-1);
                }}
              >
                <img
                  src={LeftArrow}
                  alt="left-icon"
                  className="left-arrow-icon"
                />
              </Button>
              {renderEditButton()}
            </div>
            <div className="d-flex flex-column gap-10 myprofile-container">
              <div className="myprofile-name-and-logos">
                <div className="name-container">
                  <Text className="display-extra-small">
                    {userInfo.first_name + ' ' + userInfo.last_name}
                  </Text>
                </div>
                <div className="logos-container">
                  {userInfo.user_type_id === FACULTY &&
                    userInfo?.google_scholar_url !== null && (
                      <a
                        href={userInfo?.google_scholar_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={userInfo.google_scholar_url}
                      >
                        <img
                          src={GoogleScholar}
                          alt="GoogleScholar"
                          className="myprofile-input-icon"
                        />
                      </a>
                    )}
                  {userInfo?.linkedin_url !== null && (
                    <a
                      href={userInfo?.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      key={userInfo.linkedin_url}
                    >
                      <img
                        src={LinkedIn}
                        alt="LinkedIn"
                        className="myprofile-input-icon"
                      />
                    </a>
                  )}
                  {!isMobile && renderEditButton()}
                </div>
              </div>
              <Text className="myprofile-experience">{userInfo?.headline}</Text>
              <div className="school-department-containter">
                {
                  schools.schools.find(
                    (school) => school.id === userInfo.school_id,
                  )?.name
                }
                {userInfo.school_id !== undefined &&
                  userInfo.department_id !== undefined &&
                  schools.schools.find(
                    (school) => school.id === userInfo.school_id,
                  )?.name &&
                  departments.departments.find(
                    (department) => department.id === userInfo.department_id,
                  )?.name && <span>, </span>}
                {
                  departments.departments.find(
                    (department) => department.id === userInfo.department_id,
                  )?.name
                }
              </div>
              {userInfo.location !== null &&
                userInfo.location.trim() !== '' && (
                  <div className="mp-location-container">
                    <img
                      src={Location}
                      alt="Location"
                      className="location-logo"
                    />
                    <Text>{userInfo.location}</Text>
                  </div>
                )}
              {userInfo.bio !== null && userInfo.bio.trim() !== '' && (
                <div>
                  <div className="grid gap-10">
                    <div className="line" />
                    <div
                      ref={profileBioRef}
                      className={`my-profile-bio-container ${isBioExpanded ? 'expanded' : ''}`}
                      dangerouslySetInnerHTML={{
                        __html: replaceNewlinesWithBr(userInfo.bio),
                      }}
                    />
                  </div>

                  {isBioTruncated && (
                    <Button
                      type="button"
                      onClick={handleToggleBio}
                      className="see-more-span"
                    >
                      {isBioExpanded ? 'See less' : 'See more'}
                    </Button>
                  )}
                </div>
              )}
              {isMobile && renderConversationButton()}
            </div>
          </div>
          {userInfo?.education !== null &&
            userInfo?.education !== undefined &&
            education()}
          {userInfo?.most_recent_experience &&
            userInfo?.most_recent_experience?.length !== 0 &&
            mostRecentExperiences()}
          {userInfo?.patents && userInfo?.patents?.length !== 0 && patents()}
          {userInfo?.projects && userInfo?.projects?.length !== 0 && projects()}
          {userInfo?.publications &&
            userInfo?.publications?.length !== 0 &&
            citations()}
          {userInfo?.grants && userInfo?.grants?.length !== 0 && grants()}
          {userInfo?.ideas && userInfo?.ideas?.length !== 0 && ideas()}
          {isPopupVisible && (
            <>
              <div
                className="filter-container contact-bg-color"
                onClick={handleBlurClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleBlurClick();
                  }
                }}
              >
                <div className="bottom-popup">
                  {conversationDetails && (
                    <Conversation
                      emailAddress={conversationDetails.emailAddress}
                      recipientName={conversationDetails.recipientName}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="loading-class">{message}</div>
      )}
    </>
  );
};

export default Profile;
